import React from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "@greco/components";
import { Search } from "pages/BankStatements/BankStatementsGrid/Search";
import { ResultsScore } from "components/ResultsScore";
import { Flex, FlexRight } from "components/styled";
import { useAppStoreState } from "store/AppStore";
import { useInsurerReportsStore } from "store/InsurerReportsStore";
import { InsurerReport, InsurerReportEntry } from "types/insurerReports";
import { helpAnchor } from "help/anchor";

// Bar between the master grid and the entry grid: the search box for the entries
// of the selected report (the menubar search box searches the reports), the
// selected report's number and the number of visible entries.
// ResultsScore is an emotion-styled component reading the theme, and the split
// pane is outside the ThemeProvider wrapping Navigation, so the bar brings its own.
const InsurerReportEntriesSearchBar = () => {
  const { t } = useTranslation();
  const { appState } = useAppStoreState();
  const [
    {
      entriesSearchTerm,
      selectedInsurerReportId,
      insurerReports,
      insurerReportEntries,
      insurerReportEntryMetadataMap,
    },
    { setInsurerReportsStoreValue },
  ] = useInsurerReportsStore();

  const selectedReport = insurerReports.find(
    (report: InsurerReport) =>
      report.insurerReportId === selectedInsurerReportId
  );
  const visibleEntries = insurerReportEntries.filter(
    (entry: InsurerReportEntry) =>
      insurerReportEntryMetadataMap[entry.insurerReportEntryId]?.visible ===
      true
  ).length;

  return (
    <ThemeProvider isDarkMode={appState.darkMode}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "6px 12px 6px 0",
          borderBottom: "1px solid rgba(128, 128, 128, 0.2)",
        }}
      >
        <Search
          dataHelp={helpAnchor("insurer-reports-entries-search")}
          placeholder={t("pct.searchInsurerReportEntries.label")}
          value={entriesSearchTerm}
          disabled={selectedInsurerReportId === null}
          onChange={(value) =>
            setInsurerReportsStoreValue("entriesSearchTerm", value)
          }
        />
        {selectedReport ? (
          <span style={{ fontSize: 12 }}>
            {t("pct.insurerReportNumber.label")}:{" "}
            <b>{selectedReport.insurerReportNumber}</b>
          </span>
        ) : null}
        <FlexRight>
          <Flex>
            {selectedInsurerReportId !== null ? (
              <ResultsScore
                score={visibleEntries}
                dataHelp={helpAnchor("insurer-reports-entries-count")}
              />
            ) : null}
          </Flex>
        </FlexRight>
      </div>
    </ThemeProvider>
  );
};

export default InsurerReportEntriesSearchBar;
